import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import './SlideSearchPanel.css';

/**
 * SlideSearchPanel - 검색 결과를 슬라이드 애니메이션으로 표시하는 패널
 * 모달이 아니므로 다른 UI와 자유롭게 상호작용 가능
 */
const SlideSearchPanel = ({ 
  isOpen = false,
  onClose,
  results = [],
  displayFields = [],
  onEdit,
  onDelete,
  isLoading = false,
  title = "검색 결과",
  selectedRecordId = null,        // 🎯 선택된 항목 ID 추가
  updateAnimationId = null,       // 🎯 수정 애니메이션 ID 추가
  isSearchOnly = false            // 🔍 search_only 모드 추가
}) => {
  // 애니메이션을 위한 상태 관리
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // ESC 키로 닫기 (이벤트 전파 차단)
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose?.();
    }
  }, [onClose]);

  // 애니메이션 제어
  useEffect(() => {
    if (isOpen) {
      // 열릴 때
      setIsVisible(true);
      // 더 높은 우선순위로 이벤트 리스너 등록 (capture phase 사용)
      document.addEventListener('keydown', handleKeyDown, true);
      
      // 다음 프레임에서 애니메이션 시작
      setTimeout(() => {
        setIsAnimating(true);
      }, 10);
    } else {
      // 닫힐 때
      setIsAnimating(false);
      document.removeEventListener('keydown', handleKeyDown, true);
      
      // 애니메이션 완료 후 언마운트
      setTimeout(() => {
        setIsVisible(false);
      }, 500);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen, handleKeyDown]);

  // 표시할 필드 결정
  const getDisplayFields = () => {
    if (displayFields && displayFields.length > 0) {
      // displayFields가 객체 배열인 경우 ({field, label} 형태)
      if (typeof displayFields[0] === 'object' && displayFields[0].field) {
        return displayFields.map(item => item.field);
      }
      // displayFields가 문자열 배열인 경우
      return displayFields;
    }
    
    // displayFields가 없으면 첫 번째 결과에서 자동으로 필드 추출
    if (results && results.length > 0) {
      const firstResult = results[0];
      return Object.keys(firstResult).filter(key => 
        !key.includes('_id') && 
        !key.includes('password') && 
        key !== 'id'
      ).slice(0, 6); // 최대 6개 필드만 표시
    }
    
    return [];
  };

  const fieldsToShow = getDisplayFields();

  // 필드의 라벨 정보 가져오기
  const getFieldLabel = (fieldName) => {
    // displayFields가 객체 배열인 경우 라벨 정보 활용
    if (displayFields && displayFields.length > 0 && typeof displayFields[0] === 'object') {
      const fieldObj = displayFields.find(item => item.field === fieldName);
      if (fieldObj && fieldObj.label) {
        return fieldObj.label;
      }
    }
    // 기본 한글 변환 사용
    return getFieldDisplayName(fieldName);
  };

  // 필드값 포맷팅
  const formatFieldValue = (value, fieldName) => {
    if (value === null || value === undefined) {
      return '-';
    }
    
    // 날짜 필드 포맷팅
    if (fieldName.includes('date') || fieldName.includes('time')) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          if (fieldName.includes('date') && !fieldName.includes('time')) {
            return date.toLocaleDateString('ko-KR');
          } else {
            return date.toLocaleString('ko-KR');
          }
        }
      } catch (e) {
        // 날짜 파싱 실패시 원본값 반환
      }
    }
    
    // 문자열이 너무 길면 줄임
    if (typeof value === 'string' && value.length > 50) {
      return value.substring(0, 50) + '...';
    }
    
    return String(value);
  };

  // 필드명을 한글로 변환
  const getFieldDisplayName = (fieldName) => {
    const fieldNameMap = {
      'user_name': '사용자명',
      'user_identifier': '사용자 ID',
      'vehicle_identifier': '차량 식별번호',
      'car_number': '차량번호',
      'company_name': '회사명',
      'maintenance_date': '정비일',
      'assigned_from_datetime': '할당일시',
      'incident_datetime': '사고일시',
      'phone_number': '전화번호',
      'user_role': '사용자 구분',
      'position': '직책',
      'department': '부서'
    };
    
    return fieldNameMap[fieldName] || fieldName;
  };

  // 패널이 닫혀있으면 렌더링하지 않음
  if (!isVisible) {
    return null;
  }

  // Portal을 사용해서 document.body에 직접 렌더링 (FormCanvas 제약에서 벗어남)
  return ReactDOM.createPortal(
    <div className={`slide-search-panel ${isAnimating ? 'open' : ''}`}>
      <div className="slide-search-panel-content">
        {/* 헤더 */}
        <div className="slide-search-panel-header">
          <h3 className="slide-search-panel-title">
            {title} ({results.length}개)
          </h3>
          <button 
            className="slide-search-panel-close" 
            onClick={onClose}
            aria-label="검색 결과 패널 닫기"
          >
            ✕
          </button>
        </div>

        {/* 본문 */}
        <div className="slide-search-panel-body">
          {isLoading ? (
            <div className="slide-search-loading">
              <div className="spinner"></div>
              <p>검색 중...</p>
            </div>
          ) : results.length === 0 ? (
            <div className="slide-search-empty">
              <p>검색 결과가 없습니다.</p>
            </div>
          ) : (
            <div className="slide-search-table-container">
              <table className="slide-search-table">
                <thead>
                  <tr>
                    {fieldsToShow.map(field => (
                      <th key={field}>{getFieldLabel(field)}</th>
                    ))}
                    {!isSearchOnly && <th>삭제</th>}
                  </tr>
                </thead>
                <tbody>
                  {results.map((result, index) => {
                    // 🎯 현재 행의 ID 추출
                    const currentId = result.user_id || result.log_id || result.assignment_id || 
                                      result.report_id || result.company_id || result.replacement_id ||
                                      result.maintenance_id || result.technician_id || result.id;
                    
                    // 🎯 선택된 행인지 확인
                    const isSelected = selectedRecordId && currentId && selectedRecordId === currentId;
                    
                    // 🎯 애니메이션 중인 행인지 확인
                    const isAnimating = updateAnimationId && currentId && updateAnimationId === currentId;
                    
                    // 🎯 CSS 클래스 조합
                    const rowClasses = [
                      'slide-search-row',
                      'clickable',
                      isSelected ? 'selected' : '',
                      isAnimating ? 'update-animation' : ''
                    ].filter(Boolean).join(' ');
                    
                    return (
                      <tr 
                        key={index} 
                        className={rowClasses}
                        onClick={() => onEdit?.(result)}
                        title="클릭하여 선택"
                      >
                      {fieldsToShow.map(field => (
                        <td key={field} title={result[field]}>
                          {formatFieldValue(result[field], field)}
                        </td>
                      ))}
                      {!isSearchOnly && (
                        <td>
                          <div className="slide-search-actions">
                            <button
                              onClick={(e) => {
                                e.stopPropagation(); // 행 클릭 이벤트 방지
                                onDelete?.(result);
                              }}
                              className="slide-search-action-btn delete-btn"
                              title="삭제"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                                      );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div className="slide-search-panel-footer">
          <button 
            onClick={onClose}
            className="slide-search-panel-footer-btn"
          >
            닫기
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SlideSearchPanel; 